import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Alert, Button, List, Tabs } from 'antd';
import { groupBy } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import getAvailableEndpoints, { type EndpointClass } from './getAvailableEndpoints';

export type EndpointClassSelectionProps = {
    router: RouterSpecsFragment;
    onSelect: (endpointClass: EndpointClass) => void;
};

const EndpointClassSelection = ({ router, onSelect }: EndpointClassSelectionProps) => {
    const { t } = useTranslation(['routerDetails']);
    const endpointGroups = useMemo(() => groupBy('group', getAvailableEndpoints(router, t)), [router, t]);

    const renderList = (endpoints: EndpointClass[]) => (
        <List<EndpointClass>
            dataSource={endpoints}
            renderItem={endpoint => (
                <List.Item
                    actions={[<Button icon={<ArrowRightOutlined />} onClick={() => onSelect(endpoint)} type="link" />]}
                >
                    <List.Item.Meta description={endpoint.description} title={endpoint.label} />
                </List.Item>
            )}
        />
    );

    const renderBody = () => {
        const groupCounts = Object.keys(endpointGroups).length;

        if (groupCounts === 0) {
            return <Alert description="No available endpoints for this router" message="Error" type="error" showIcon />;
        }

        if (groupCounts === 1) {
            return renderList(Object.values(endpointGroups)[0]);
        }

        return (
            <Tabs tabPosition="left">
                {Object.entries(endpointGroups).map(([group, endpoints]) => (
                    <Tabs.TabPane key={group} tab={group}>
                        {renderList(endpoints)}
                    </Tabs.TabPane>
                ))}
            </Tabs>
        );
    };

    return <Card>{renderBody()}</Card>;
};

export default EndpointClassSelection;
